import type {
  CreateFiscalProfilePayload,
  FiscalProfile,
  FiscalTaxType,
  IvaFrequency,
  IvaRegime,
  UpdateFiscalProfilePayload,
} from "@/services/fiscalService";
import {
  findExonerationLegendIdByText,
  getLegendCategory,
  LEGEND_OPTIONS_BY_CATEGORY,
  type LegendCategory,
} from "@/lib/fiscalProfileLegendOptions";
import type { FiscalProfileEditorValues } from "@/lib/fiscalProfileEditorTypes";

export type ExoneratedRegimePayload = {
  exoneratedRegimeEnabled: boolean | null;
  exoneratedRegimeLegend: string | null;
  exoneratedRegimeHeaderLegend: string | null;
};

/**
 * Maps a regime to the DJ frequency the tenant files at.
 *   - minimum (Literal E) → flat monthly mínimo, no DJ math
 *   - cede                 → monthly DJ
 *   - no_cede              → annual DJ + monthly anticipos
 *   - servicios_personales → bimonthly anticipos + annual DJ
 *
 * Driving frequency off regime in one place keeps the form, the API
 * payload, and the dashboard summary all reading the same story.
 */
export function ivaFrequencyForRegime(regime: IvaRegime): IvaFrequency {
  switch (regime) {
    case "minimum":
      return "minimum";
    case "cede":
      return "monthly";
    case "no_cede":
      return "annual";
    case "servicios_personales":
      return "bimonthly";
  }
}

// Record<Union, true> rather than a Union[] on purpose: a missing key is a
// compile error, so adding a variant to IvaRegime / IvaFrequency cannot leave
// it out of these sets. A plain array would still typecheck while the
// sanitisers below silently blanked the new value as unrecognised.
const IVA_REGIMES: Record<IvaRegime, true> = {
  minimum: true,
  cede: true,
  no_cede: true,
  servicios_personales: true,
};

const IVA_FREQUENCIES: Record<IvaFrequency, true> = {
  monthly: true,
  minimum: true,
  annual: true,
  bimonthly: true,
};

/** Own-property only, so inherited names like "toString" are not regimes. */
function isKeyOf(set: Record<string, true>, v: string | null | undefined): boolean {
  return v != null && Object.prototype.hasOwnProperty.call(set, v);
}

/**
 * The stored regime arrives typed as `string | null` from the API
 * (fiscalProfileService), so a value outside the union is reachable. Treating
 * one as a regime would send it through ivaFrequencyForRegime, whose switch has
 * no default and would hand back `undefined` for it — so an unrecognised value
 * is undeclared, which is the same answer we give for null.
 */
function asDeclaredRegime(stored: string | null | undefined): IvaRegime | "" {
  return isKeyOf(IVA_REGIMES, stored) ? (stored as IvaRegime) : "";
}

/** Same reasoning as asDeclaredRegime: the API types this one `string | null` too. */
function asKnownFrequency(
  stored: string | null | undefined,
): IvaFrequency | "" {
  return isKeyOf(IVA_FREQUENCIES, stored) ? (stored as IvaFrequency) : "";
}

/**
 * How the régimen select should read a possibly-undeclared regime.
 *
 * ONE home for "an unset regime is not 'minimum'". The select used to coerce
 * `watch("ivaRegime") || "minimum"` at the call site, which made an
 * undeclared tenant indistinguishable from a deliberate "IVA mínimo" tick —
 * and since the lock keys on the regime being 'minimum', they were shown
 * Pequeña Empresa pre-selected with the select disabled and no way out.
 * An empty regime stays empty: no lock, no derived frequency to describe.
 *
 * Takes a bare `string` on purpose, NOT `IvaRegime | ""`: sanitising an
 * untrusted stored value is this function's job, and narrowing the parameter
 * would only push the unchecked cast back out to the callers. Do not tighten.
 */
export function resolveIvaRegimeSelection(stored: string | null | undefined): {
  regime: IvaRegime | "";
  isLockedToMinimum: boolean;
  derivedFrequency: IvaFrequency | null;
} {
  const regime = asDeclaredRegime(stored);
  return {
    regime,
    isLockedToMinimum: regime === "minimum",
    derivedFrequency: regime ? ivaFrequencyForRegime(regime) : null,
  };
}

export function getEmptyFiscalProfileEditorValues(): FiscalProfileEditorValues {
  // No taxes / regimes / frequencies pre-checked: the accountant (or owner)
  // must mark each obligation explicitly. INDUSTRY_FISCAL_DEFAULTS overrides
  // some of these when an industry is known, but never the IVA regime or
  // frequency — DGI assigns those by turnover, not by line of business.
  return {
    companyType: "sas",
    activityStartDate: "",
    hasEmployees: false,
    paysFonasaServiciosPersonales: false,
    isExportingServices: false,
    taxes: [],
    ivaFrequency: "",
    ivaRegime: "",
    ivaMinimumBenefit: "",
    fiscalBenefitOptIn: false,
    iraeFrequency: "",
    fiscalYearEnd: "12-31",
    bpsCompanyNumber: "",
    hasExoneration: false,
    specialRegime: "",
    exonerationLegendId: "custom",
    exonerationLineLegend: "",
    exonerationHeaderLegend: "",
    // Off, not "unset reads as on". The column is NOT NULL DEFAULT false and
    // the cron matches `= true`, so anything other than an explicit opt-in
    // means the monthly BPS request does not fire.
    autoRequestBpsBoleto: false,
  };
}

/**
 * The subset of tenant fields the editor actually reads. Kept structural (not
 * `TenantCurrentResponse`) so the accountant cartera — which fetches only the
 * exonerated-regime slice for a client it is not logged in as — can hydrate the
 * same form as the SMB self-service settings page.
 */
export interface FiscalProfileEditorTenantSource {
  industry?: string | null;
  companyType?: string | null;
  exoneratedRegimeEnabled?: boolean | null;
  exoneratedRegimeLegend?: string | null;
  exoneratedRegimeHeaderLegend?: string | null;
}

function resolveCategoryForTenant(
  tenant: FiscalProfileEditorTenantSource | null
): LegendCategory {
  return getLegendCategory(tenant?.industry ?? null);
}

/** Build form defaults from fiscal profile API + tenant exoneration fields. */
export function fiscalProfileEditorValuesFromSources(
  profile: FiscalProfile | null,
  tenant: FiscalProfileEditorTenantSource | null
): FiscalProfileEditorValues {
  const base = getEmptyFiscalProfileEditorValues();
  const category = resolveCategoryForTenant(tenant);

  if (tenant?.companyType === "unipersonal" ||
    tenant?.companyType === "sas" ||
    tenant?.companyType === "srl" ||
    tenant?.companyType === "sa") {
    base.companyType = tenant.companyType;
  }

  if (profile) {
    base.companyType = profile.companyType;
    base.hasEmployees = profile.hasEmployees;
    // null (never asked) loads as false — same rendering as an explicit no.
    base.paysFonasaServiciosPersonales =
      profile.paysFonasaServiciosPersonales === true;
    base.isExportingServices = profile.isExporter;
    base.taxes = profile.taxes.slice();
    // A NULL regime means UNDECLARED, so it hydrates as "" — the same state
    // getEmptyFiscalProfileEditorValues starts from. Defaulting it to
    // "minimum" (and the frequency to "monthly") invented a declaration the
    // tenant never made: the select then locks to Pequeña Empresa, and a
    // profile deliberately cleared because we do not know the DGI grupo came
    // straight back as IVA mínimo.
    //
    // Frequency is derived from regime everywhere else in this module, so it
    // is only meaningful alongside a declared one. A stored 'cede' with a null
    // frequency keeps the frequency its regime implies — blanking it would
    // drop the IVA line out of the profile summary (ivaMessageKey("") is
    // undefined) for a tenant whose regime we do know. An undeclared regime
    // blanks the frequency outright, INCLUDING when a frequency is stored: a
    // concrete frequency with no regime is a state nothing downstream can read.
    const storedSelection = resolveIvaRegimeSelection(profile.ivaRegime);
    base.ivaRegime = storedSelection.regime;
    base.ivaFrequency = storedSelection.regime
      ? asKnownFrequency(profile.ivaFrequency) ||
        ivaFrequencyForRegime(storedSelection.regime)
      : "";
    base.specialRegime = profile.specialRegime ?? "";
    base.ivaMinimumBenefit = profile.ivaMinimumBenefit ?? "";
    base.fiscalBenefitOptIn = profile.fiscalBenefitOptIn ?? false;
    base.iraeFrequency = profile.iraeFrequency ?? "annual";
    base.fiscalYearEnd = profile.fiscalYearEnd;
    base.activityStartDate = profile.activityStartDate.slice(0, 10);
    base.bpsCompanyNumber = profile.bpsCompanyNumber ?? "";
    // `=== true`, never `!== false`: a profile fetched from an older surface
    // (or the accountant slice) can omit the field entirely, and coercing that
    // absence to on would show a ticked box for an automation that is not
    // running — and then send `true` back on the next save.
    base.autoRequestBpsBoleto = profile.autoRequestBpsBoleto === true;
  }

  if (tenant) {
    base.hasExoneration = tenant.exoneratedRegimeEnabled ?? false;
    const line = tenant.exoneratedRegimeLegend ?? "";
    const header = tenant.exoneratedRegimeHeaderLegend ?? "";
    base.exonerationLineLegend = line;
    base.exonerationHeaderLegend = header;
    // findExonerationLegendIdByText resolves against `category`, which is
    // derived from this same tenant.industry — so the id it returns is always
    // either "custom" or a member of that industry's own option list. A guard
    // re-checking membership against getLegendOptionsForIndustry(industry)
    // used to sit here; it could never fire, and coverage confirmed it never
    // did. A legend stored under a different industry already resolves to
    // "custom" through the lookup itself.
    base.exonerationLegendId = findExonerationLegendIdByText(category, line);
  }

  return base;
}

/**
 * MM-DD with real month and day ranges. Exported so every reader of
 * fiscalYearEnd agrees on what is valid — the summary builder used to accept
 * any two-digit pair and happily render "13/45".
 */
export const FISCAL_YEAR_END_PATTERN =
  /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

export function validateFiscalProfileEditor(
  values: FiscalProfileEditorValues,
  options?: { hideAgendaFields?: boolean }
): boolean {
  const fiscalYearEndValid = FISCAL_YEAR_END_PATTERN.test(
    values.fiscalYearEnd.trim()
  );
  const activityStartValid =
    values.activityStartDate.trim() !== "" &&
    !Number.isNaN(Date.parse(values.activityStartDate));
  if (options?.hideAgendaFields) {
    return fiscalYearEndValid && activityStartValid;
  }
  // "IVA mínimo" (regime=minimum) is an IVA obligation even though its checkbox
  // only flips the regime and never adds "IVA" to the taxes array (persistence
  // adds it on save). So a Literal E tenant who ticks only "IVA mínimo" has an
  // empty taxes array — count the minimum regime as satisfying the "≥1 tax"
  // requirement, otherwise "Confirmar y seguir" stays disabled for them.
  const hasObligation =
    values.taxes.length >= 1 || values.ivaRegime === "minimum";
  return hasObligation && fiscalYearEndValid && activityStartValid;
}

export function toggleTaxInList(
  taxes: FiscalTaxType[],
  tax: FiscalTaxType
): FiscalTaxType[] {
  return taxes.includes(tax) ? taxes.filter((t) => t !== tax) : [...taxes, tax];
}

function isIvaRegime(value: unknown): value is IvaRegime {
  return (
    value === "minimum" ||
    value === "cede" ||
    value === "no_cede" ||
    value === "servicios_personales"
  );
}

export function toUpdateFiscalProfilePayload(
  values: FiscalProfileEditorValues,
  options: {
    hideAgendaFields?: boolean;
    /** When hiding agenda fields on update, keep prior taxes from API. */
    taxesWhenHidden?: FiscalTaxType[];
    /** When hiding agenda fields on update, keep prior BPS number from API. */
    bpsWhenHidden?: string | null;
  }
): UpdateFiscalProfilePayload {
  const rawTaxes: FiscalTaxType[] = options.hideAgendaFields
    ? (options.taxesWhenHidden ?? values.taxes)
    : values.taxes;
  // "IVA mínimo" (regime=minimum) implies an IVA obligation even if the user
  // didn't tick the plain IVA box — the fiscal-agenda gates monthly IVA
  // obligation generation on taxes.includes('IVA'), so we add it here to
  // keep Literal E tenants on the agenda.
  const taxesForApi: FiscalTaxType[] =
    values.ivaRegime === "minimum" && !rawTaxes.includes("IVA")
      ? [...rawTaxes, "IVA"]
      : rawTaxes;

  const ivaRegimePayload =
    taxesForApi.includes("IVA") && isIvaRegime(values.ivaRegime)
      ? values.ivaRegime
      : null;
  // Frequency is derived from regime, never set independently — guarantees
  // the dashboard's per-period IVA branch lines up with whatever the UI says.
  const ivaFreq = ivaRegimePayload
    ? ivaFrequencyForRegime(ivaRegimePayload)
    : null;
  // Literal E always gets the e-Factura benefit (3,3% of monthly billing, capped
  // at the standard IVA mínimo). Since every Fiscalo tenant invoices via e-Factura
  // the benefit always applies, so there's no standard/efactura choice — force
  // 'efactura_3_3' for minimum, null for any other regime (so a tenant flipping
  // CEDE → minimum → CEDE doesn't leave a stale value).
  const ivaMinimumBenefitPayload =
    ivaRegimePayload === "minimum" ? "efactura_3_3" : null;
  // The 80 UI benefit opt-in is a régimen-general self-declaration; Literal E
  // (minimum) gets the benefit automatically, so we send null there.
  const fiscalBenefitOptInPayload =
    ivaRegimePayload !== null && ivaRegimePayload !== "minimum"
      ? values.fiscalBenefitOptIn
      : null;
  const iraeFreq =
    taxesForApi.includes("IRAE") &&
    (values.iraeFrequency === "annual" || values.iraeFrequency === "monthly")
      ? values.iraeFrequency
      : undefined;

  const bpsPayload = options.hideAgendaFields
    ? (options.bpsWhenHidden ?? null)
    : values.bpsCompanyNumber.trim() || null;

  return {
    companyType: values.companyType,
    isExporter: values.isExportingServices,
    hasEmployees: values.hasEmployees,
    paysFonasaServiciosPersonales: values.paysFonasaServiciosPersonales,
    taxes: taxesForApi,
    ivaFrequency: ivaFreq,
    ivaRegime: ivaRegimePayload,
    // Orthogonal to ivaRegime; "" means no special regime, sent as null so a
    // tenant can be taken back OUT of one.
    specialRegime: values.specialRegime.trim() || null,
    ivaMinimumBenefit: ivaMinimumBenefitPayload,
    fiscalBenefitOptIn: fiscalBenefitOptInPayload,
    iraeFrequency: iraeFreq ?? null,
    fiscalYearEnd: values.fiscalYearEnd.trim(),
    activityStartDate: values.activityStartDate.trim(),
    bpsCompanyNumber: bpsPayload,
    // Sent on every update, including when the section is hidden: hydration
    // seeds the form from the stored value, so a hidden toggle round-trips
    // what is already there instead of silently clearing it.
    autoRequestBpsBoleto: values.autoRequestBpsBoleto,
  };
}

export function toCreateFiscalProfilePayload(
  values: FiscalProfileEditorValues,
  options: { hideAgendaFields?: boolean; taxesWhenHidden?: FiscalTaxType[] }
): CreateFiscalProfilePayload {
  const rawTaxes: FiscalTaxType[] = options.hideAgendaFields
    ? (options.taxesWhenHidden ?? values.taxes)
    : values.taxes;
  const taxesForApi: FiscalTaxType[] =
    values.ivaRegime === "minimum" && !rawTaxes.includes("IVA")
      ? [...rawTaxes, "IVA"]
      : rawTaxes;

  const ivaRegimePayload =
    taxesForApi.includes("IVA") && isIvaRegime(values.ivaRegime)
      ? values.ivaRegime
      : null;
  const ivaFreq = ivaRegimePayload
    ? ivaFrequencyForRegime(ivaRegimePayload)
    : undefined;
  // Literal E always gets the e-Factura benefit (see toUpdate... above).
  const ivaMinimumBenefitPayload =
    ivaRegimePayload === "minimum" ? "efactura_3_3" : null;
  const fiscalBenefitOptInPayload =
    ivaRegimePayload !== null && ivaRegimePayload !== "minimum"
      ? values.fiscalBenefitOptIn
      : null;
  const iraeFreq =
    taxesForApi.includes("IRAE") &&
    (values.iraeFrequency === "annual" || values.iraeFrequency === "monthly")
      ? values.iraeFrequency
      : undefined;

  return {
    companyType: values.companyType,
    isExporter: values.isExportingServices,
    hasEmployees: values.hasEmployees,
    paysFonasaServiciosPersonales: values.paysFonasaServiciosPersonales,
    taxes: taxesForApi,
    ivaFrequency: ivaFreq,
    ivaRegime: ivaRegimePayload ?? undefined,
    specialRegime: values.specialRegime.trim() || null,
    ivaMinimumBenefit: ivaMinimumBenefitPayload,
    fiscalBenefitOptIn: fiscalBenefitOptInPayload,
    iraeFrequency: iraeFreq,
    fiscalYearEnd: values.fiscalYearEnd.trim(),
    activityStartDate: values.activityStartDate.trim(),
    bpsCompanyNumber: options.hideAgendaFields
      ? undefined
      : values.bpsCompanyNumber.trim() || undefined,
    autoRequestBpsBoleto: values.autoRequestBpsBoleto,
  };
}

export function toExoneratedRegimePayload(
  values: FiscalProfileEditorValues,
  industry: string | null
): ExoneratedRegimePayload {
  if (!values.hasExoneration) {
    return {
      exoneratedRegimeEnabled: false,
      exoneratedRegimeLegend: null,
      exoneratedRegimeHeaderLegend: null,
    };
  }
  const category = getLegendCategory(industry);
  const options = LEGEND_OPTIONS_BY_CATEGORY[category];
  const selected = options.find((o) => o.id === values.exonerationLegendId);
  const line =
    selected?.legendText != null
      ? selected.legendText.trim()
      : values.exonerationLineLegend.trim();
  const header = values.exonerationHeaderLegend.trim() || line;
  return {
    exoneratedRegimeEnabled: true,
    exoneratedRegimeLegend: line || null,
    exoneratedRegimeHeaderLegend: header || null,
  };
}
